import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { Clock, ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"

function CoachArticleCard({ article }) {
  const formattedDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null

  return (
    <Link
      href={`/collective/${article.category.slug}/${article.slug}`}
      className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
    >
      {/* Image */}
      <div className="aspect-[2/1] relative overflow-hidden bg-zinc-100">
        {article.featuredImageUrl ? (
          <img
            src={article.featuredImageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-kf-red/20 to-kf-red/5 flex items-center justify-center">
            <span className="text-kf-red/40 font-display text-2xl">KF</span>
          </div>
        )}
        <span className="absolute top-3 left-3 px-3 py-1 bg-kf-red text-white text-xs font-semibold uppercase tracking-wider">
          {article.category.name}
        </span>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-display text-lg text-zinc-900 mb-2 group-hover:text-kf-red transition-colors line-clamp-2">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="text-zinc-600 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
        )}
        <div className="flex items-center gap-4 text-sm text-zinc-500">
          {formattedDate && <span>{formattedDate}</span>}
          {article.readTime && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{article.readTime} min read</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}

export default function CoachShow({ seo, coach = {}, articles = [], settings = {} }) {
  const { scheduleCallUrl = "#" } = settings

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="collective" scheduleCallUrl={scheduleCallUrl} />

        {/* Coach Profile Header */}
        <section className="bg-zinc-50 pt-32 pb-12">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            {/* Back link */}
            <Link
              href="/collective/coaches"
              className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              All Coaches
            </Link>

            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* Headshot */}
              <div className="w-40 h-40 rounded-full overflow-hidden bg-zinc-100 flex-shrink-0">
                {coach.headshotUrl ? (
                  <img
                    src={coach.headshotUrl}
                    alt={coach.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-kf-gradient flex items-center justify-center">
                    <span className="text-white text-4xl font-display">
                      {coach.name?.charAt(0) || "K"}
                    </span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="text-center md:text-left flex-1">
                <h1 className="font-display text-3xl md:text-4xl text-zinc-900 mb-2">
                  {coach.name}
                </h1>
                {(coach.jobTitle || coach.company) && (
                  <p className="text-zinc-500 mb-3">
                    {[coach.jobTitle, coach.company].filter(Boolean).join(" at ")}
                  </p>
                )}
                {coach.bio && (
                  <p className="text-zinc-600 mb-4 max-w-xl">{coach.bio}</p>
                )}
                <p className="text-sm text-zinc-400 uppercase tracking-wider mb-4">
                  {coach.articleCount} {coach.articleCount === 1 ? "article" : "articles"} published
                </p>
                {coach.scheduleCallUrl && coach.scheduleCallUrl !== "#" && (
                  <Button variant="gradient" asChild>
                    <a href={coach.scheduleCallUrl} target="_blank" rel="noopener noreferrer">Schedule a Call</a>
                  </Button>
                )}
                {coach.profileArticleUrl && (
                  <Button variant="kf-outline" asChild className="ml-2">
                    <Link href={coach.profileArticleUrl}>Read My Story</Link>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Articles by Coach */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <h2 className="font-display text-2xl md:text-3xl text-zinc-900 mb-8">
              Articles by {coach.name}
            </h2>

            {articles.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <CoachArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <p className="text-zinc-500 text-center py-8">
                No published articles yet.
              </p>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-kf-gradient py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl text-white mb-4">
              READY TO TRANSFORM YOUR BUSINESS?
            </h2>
            <p className="text-white/80 mb-8">
              Join our community of faith-driven leaders and start your journey today.
            </p>
            {scheduleCallUrl && scheduleCallUrl !== "#" && (
              <Button variant="kf-secondary" asChild>
                <a href={scheduleCallUrl} target="_blank" rel="noopener noreferrer">Schedule a Call</a>
              </Button>
            )}
          </div>
        </section>

        <Footer activePage="collective" />
      </div>
    </>
  )
}
